import { useLanguage } from "@/context/LanguageContext";
import { useId } from "react";
import { QuoteButton } from "@/components/shared";
import { SITE, VEHICLE_PREFERENCES } from "@/config/site";
import { VEHICLE_TARIFFS, TARIFF_POLICY, formatINR, getVehicleTariff } from "@/config/tariffs";

const CATEGORIES = ["Sedan", "SUV / MPV", "Tempo Traveller"];
const optionId = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function VehicleTariffTable({ category, pickupCity = SITE.baseCity }) {
  const { t, cityName } = useLanguage();
  const captionId = useId();
  const noteId = useId();
  const categories = category ? CATEGORIES.filter((item) => item === category) : CATEGORIES;
  const tariffs = VEHICLE_TARIFFS.filter((vehicle) => categories.includes(vehicle.category));
  const unpriced = categories.flatMap((vehicleCategory) =>
    (VEHICLE_PREFERENCES[vehicleCategory] || [])
      .filter((preference) => preference !== "No preference" && preference !== "Help me choose" && !getVehicleTariff(preference))
      .map((preference) => ({ category: vehicleCategory, preference })),
  );

  return (
    <div data-testid="vehicle-tariff-table">
      <p id={noteId} className="mb-4 text-sm leading-relaxed text-slate-600">
        {t("Outstation rates include fuel with a minimum of {outstationMinKmPerDay} KM per day. Local base fares cover {localKm} KM / {localHours} hours, plus fuel. {gstPercent}% GST and applicable extras are additional. Scroll the table sideways on a smaller screen.", { outstationMinKmPerDay: TARIFF_POLICY.outstationMinKmPerDay, localKm: TARIFF_POLICY.localKm, localHours: TARIFF_POLICY.localHours, gstPercent: TARIFF_POLICY.gstPercent })}
      </p>
      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        role="region"
        aria-labelledby={captionId}
        tabIndex={0}
        data-testid="vehicle-tariff-scroll"
      >
        <table className="min-w-[1020px] w-full border-collapse text-left text-sm" aria-describedby={noteId}>
          <caption id={captionId} className="bg-slate-900 px-5 py-4 text-left text-base font-bold text-white">
            {t("{tariffTitle} · {city}", { tariffTitle: t(category === "Tempo Traveller" ? "Tempo Traveller hire and rental tariffs" : "Vehicle hire and rental tariffs"), city: cityName(pickupCity) })}
          </caption>
          <thead className="bg-slate-100 text-slate-900">
            <tr>
              <th scope="col" className="px-4 py-4">{t("Vehicle")}</th>
              <th scope="col" className="px-4 py-4">{t("Passenger + driver seats")}</th>
              <th scope="col" className="px-4 py-4">{t("Outstation / KM")}<span className="block font-normal text-slate-600">{t("Fuel included")}</span></th>
              <th scope="col" className="px-4 py-4">{t("Local base + fuel")}<span className="block font-normal text-slate-600">{t("{localKm} KM / {localHours} hours", { localKm: TARIFF_POLICY.localKm, localHours: TARIFF_POLICY.localHours })}</span></th>
              <th scope="col" className="px-4 py-4">{t("Extra / hour")}<span className="block font-normal text-slate-600">{t("After {localHours} hours", { localHours: TARIFF_POLICY.localHours })}</span></th>
              <th scope="col" className="px-4 py-4">{t("Night charge")}</th>
              <th scope="col" className="px-4 py-4">{t("Your trip quote")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {tariffs.map((vehicle) => (
              <tr key={vehicle.id} className="align-top hover:bg-amber-50/50" data-testid={`tariff-row-${vehicle.id}`}>
                <th scope="row" className="px-4 py-4 font-bold text-slate-900">
                  {vehicle.name}
                  <span className="mt-1 block text-xs font-normal text-slate-500">{t(vehicle.category)}</span>
                </th>
                <td className="px-4 py-4 text-slate-700">
                  {t("{passengerSeats} passengers + {driverSeats} driver", { passengerSeats: vehicle.passengerSeats, driverSeats: vehicle.driverSeats })}
                  <span className="mt-1 block text-xs text-slate-500">{t("{totalSeats} seats total", { totalSeats: vehicle.totalSeats })}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-4 font-bold text-slate-900" data-testid={`tariff-outstation-${vehicle.id}`}>{t("{amount} / KM", { amount: formatINR(vehicle.outstationRatePerKm) })}</td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700" data-testid={`tariff-local-${vehicle.id}`}>{t("{amount} + fuel", { amount: formatINR(vehicle.localBaseFare) })}</td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">{t("{amount} / hour", { amount: formatINR(vehicle.extraHour) })}</td>
                <td className="whitespace-nowrap px-4 py-4 text-slate-700">{t("{amount} / night", { amount: formatINR(vehicle.nightCharge) })}</td>
                <td className="px-4 py-4">
                  <QuoteButton
                    prefill={{ pickup_city: pickupCity, vehicle_category: vehicle.category, vehicle_preference: vehicle.preference }}
                    testId={`tariff-quote-${vehicle.id}`}
                    className="whitespace-nowrap px-4 py-2 text-sm"
                    ariaLabel={t("Get quote for {vehicleName}", { vehicleName: vehicle.name })}
                  >
                    {t("Get quote")}
                  </QuoteButton>
                </td>
              </tr>
            ))}
            {unpriced.map((vehicle) => (
              <tr key={`${vehicle.category}:${vehicle.preference}`} className="align-top bg-slate-50" data-testid={`tariff-unpriced-${optionId(vehicle.preference)}`}>
                <th scope="row" className="px-4 py-4 font-bold text-slate-900">
                  {t(vehicle.preference)}{vehicle.category === "Tempo Traveller" ? " Tempo Traveller" : ""}
                </th>
                <td className="px-4 py-4 text-slate-600">{t("Confirm passenger capacity and luggage space")}</td>
                <td colSpan={4} className="px-4 py-4 text-slate-700">
                  <span className="font-semibold">{t("Quote on request")}</span>
                  <span className="mt-1 block text-xs">{t("Confirm local, outstation, extra-hour and night charges for this option.")}</span>
                </td>
                <td className="px-4 py-4">
                  <QuoteButton
                    prefill={{ pickup_city: pickupCity, vehicle_category: vehicle.category, vehicle_preference: vehicle.preference }}
                    testId={`tariff-quote-${optionId(vehicle.preference)}`}
                    className="whitespace-nowrap px-4 py-2 text-sm"
                    ariaLabel={t("Request quote for {vehiclePreference}", { vehiclePreference: t(vehicle.preference) })}
                  >
                    {t("Request quote")}
                  </QuoteButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VehicleTariffTable;
